import React, { useEffect, useMemo, useState } from 'react';
import ParameterGroup from './ParameterGroup';
import ParameterAlgorithmTabs from './ParameterAlgorithmTabs';
import ParameterControl from './ParameterControl';
import Tooltip from '../common/Tooltip';
import { getTooltip } from '../../config/tooltips';
import './ParameterPanel.css';

const ParameterPanel = ({ groups, values, onChange, disabled, className = '', constraints = {} }) => {
  const safeGroups = useMemo(() => (Array.isArray(groups) ? groups : []), [groups]);

  const sections = useMemo(() => {
    const byRoot = new Map();

    safeGroups.forEach((group, index) => {
      const rootId = group.rootId || group.id;
      if (!rootId) {
        return;
      }

      if (!byRoot.has(rootId)) {
        byRoot.set(rootId, {
          id: rootId,
          order: index,
          rootGroup: null,
          algorithmGroups: [],
          extraGroups: [],
        });
      }

      const bucket = byRoot.get(rootId);
      bucket.order = Math.min(bucket.order, index);

      if (group.id === rootId) {
        bucket.rootGroup = group;
        bucket.title = group.title;
        bucket.description = group.description;
      } else if (group.kind === 'algorithm') {
        bucket.algorithmGroups.push(group);
      } else {
        bucket.extraGroups.push(group);
      }
    });

    return Array.from(byRoot.values())
      .map((bucket) => ({
        ...bucket,
        title: bucket.title || bucket.extraGroups[0]?.title || bucket.id,
        description: bucket.description || bucket.rootGroup?.description || '',
      }))
      .sort((a, b) => a.order - b.order);
  }, [safeGroups]);

  const [openSectionId, setOpenSectionId] = useState(null);

  useEffect(() => {
    if (!sections.length) {
      setOpenSectionId(null);
      return;
    }
    if (!openSectionId || !sections.some((section) => section.id === openSectionId)) {
      setOpenSectionId(sections[0].id);
    }
  }, [sections, openSectionId]);

  if (!sections.length) {
    return null;
  }

  const resolveRootGroup = (section, excludeIds) => {
    const candidate = section.rootGroup;
    if (!candidate) {
      return null;
    }

    let filteredControls = candidate.controls || [];
    if (section.algorithmGroups?.length) {
      filteredControls = filteredControls.filter((control) => control.name !== 'algorithm');
    }
    if (excludeIds.length) {
      const excludeSet = new Set(excludeIds);
      filteredControls = filteredControls.filter((control) => !excludeSet.has(control.id));
    }

    if (!filteredControls.length) {
      return null;
    }

    return {
      ...candidate,
      controls: filteredControls,
    };
  };

  const resolveEnabledState = (toggleControl) => {
    if (!toggleControl) {
      return true;
    }
    const value = values?.[toggleControl.id];
    if (typeof value === 'boolean') {
      return value;
    }
    if (value === '' || value === undefined || value === null) {
      if (typeof toggleControl.defaultValue === 'boolean') {
        return toggleControl.defaultValue;
      }
      return true;
    }
    return Boolean(value);
  };

  const readValue = (controlId) => {
    if (!values) {
      return undefined;
    }
    return values[controlId];
  };

  const formatNumber = (value, options = {}) => {
    if (value === null || value === undefined) {
      return null;
    }
    const parsed = typeof value === 'number' ? value : Number(value);
    if (Number.isNaN(parsed)) {
      return null;
    }
    const { suffix = '', precision = 2, compact = false } = options;
    if (compact && Math.abs(parsed) >= 1000) {
      return `${Math.round(parsed / 100) / 10}k${suffix}`;
    }
    return `${parsed.toFixed(precision)}${suffix}`;
  };

  const resolveSummary = (sectionId) => {
    if (!sectionId) {
      return '';
    }

    if (sectionId.includes('depth_generation')) {
      const minDepth = formatNumber(readValue('depth_generation.min_depth'), { precision: 2 });
      const maxDepth = formatNumber(readValue('depth_generation.max_depth'), { precision: 2 });
      if (minDepth && maxDepth) {
        return `Depth ${minDepth}–${maxDepth} m`;
      }
    }

    if (sectionId.includes('pointcloud_generation')) {
      const maxPoints = formatNumber(readValue('pointcloud_generation.max_points'), {
        compact: true,
        precision: 1,
        suffix: ' pts',
      });
      if (maxPoints) {
        return `Max ${maxPoints}`;
      }
    }

    if (sectionId.includes('mesh_generation')) {
      const algorithm = readValue('mesh_generation.algorithm');
      if (algorithm) {
        const label = algorithm
          .split('_')
          .map((segment) => (segment ? segment[0].toUpperCase() + segment.slice(1) : segment))
          .join(' ');
        return `Algorithm: ${label}`;
      }
    }

    return '';
  };

  return (
    <div className={`parameter-panel parameter-panel--stacked ${className}`}>
      {sections.map((section, index) => {
        const toggleControl = section.rootGroup?.controls?.find((control) => control.name === '__enabled');
        const toggleConstraint = toggleControl ? constraints?.[toggleControl.id] : null;
        const sectionEnabled = resolveEnabledState(toggleControl);
        const isCollapsed = Boolean(toggleControl) && !sectionEnabled;
        const isOpen = openSectionId === section.id && !isCollapsed;
        const sectionTooltip = getTooltip(`${section.id}.__enabled`);
        const rootGroup = resolveRootGroup(section, toggleControl ? [toggleControl.id] : []);
        const hasContent = Boolean(
          rootGroup ||
            (section.algorithmGroups?.length ?? 0) ||
            (section.extraGroups?.length ?? 0)
        );
        const summaryText = resolveSummary(section.id);

        return (
          <section
            key={section.id}
            className={`parameter-section${isCollapsed ? ' parameter-section--collapsed' : ''}`}
          >
            <div className="parameter-section__header">
              <button
                type="button"
                className="parameter-section__trigger"
                onClick={hasContent ? () => setOpenSectionId(section.id) : undefined}
                aria-expanded={hasContent ? isOpen : false}
                disabled={isCollapsed}
              >
                <div className="parameter-section__titles">
                  <div className="parameter-section__title-row">
                    <h3>{section.title}</h3>
                    {sectionTooltip ? (
                      <Tooltip title={sectionTooltip.title} description={sectionTooltip.description} position="top">
                        <svg
                          className="parameter-section__info"
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <circle cx="12" cy="12" r="10" />
                          <path d="M9,9h0a3,3,0,0,1,6,0c0,2-3,3-3,3" />
                          <path d="M12,17h0" />
                        </svg>
                      </Tooltip>
                    ) : null}
                  </div>
                  {summaryText ? (
                    <p className="parameter-section__summary">{summaryText}</p>
                  ) : (
                    section.description ? (
                      <p className="parameter-section__summary">{section.description}</p>
                    ) : null
                  )}
                </div>
                {hasContent ? (
                  <span
                    className={`parameter-section__chevron${
                      isOpen ? ' parameter-section__chevron--open' : ''
                    }`}
                    aria-hidden="true"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M6 9l6 6 6-6" />
                    </svg>
                  </span>
                ) : null}
              </button>

              {toggleControl ? (
                <div className="parameter-section__toggle">
                  <ParameterControl
                    control={toggleControl}
                    value={values?.[toggleControl.id] ?? toggleControl.defaultValue ?? true}
                    onChange={onChange}
                    disabled={disabled}
                    constraint={toggleConstraint}
                  />
                </div>
              ) : null}
            </div>

            {isOpen && hasContent ? (
              <div
                className={`parameter-section__body${isOpen ? ' parameter-section__body--open' : ''}`}
                aria-hidden={!isOpen}
              >
                {rootGroup ? (
                  <ParameterGroup
                    key={rootGroup.id}
                    group={rootGroup}
                    values={values}
                    onChange={onChange}
                    disabled={disabled}
                    constraints={constraints}
                    hideHeader={rootGroup.id === section.id}
                  />
                ) : null}

                {section.algorithmGroups?.length ? (
                  <ParameterAlgorithmTabs
                    key={`${section.id}__algorithms`}
                    rootGroup={section.rootGroup}
                    algorithmGroups={section.algorithmGroups}
                    values={values}
                    onChange={onChange}
                    disabled={disabled}
                    constraints={constraints}
                  />
                ) : null}

                {section.extraGroups?.map((group) => (
                  <ParameterGroup
                    key={group.id}
                    group={group}
                    values={values}
                    onChange={onChange}
                    disabled={disabled}
                    constraints={constraints}
                  />
                ))}
              </div>
            ) : null}

            <div
              className={`parameter-section__collapsed-message${isCollapsed ? ' parameter-section__collapsed-message--visible' : ''}`}
              aria-hidden={!isCollapsed}
            >
              Step disabled. Enable this stage to configure its parameters.
            </div>
          </section>
        );
      })}
    </div>
  );
};

export default ParameterPanel;
